import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { SystemSetting } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";

type PublicSystemSettings = {
  appName: string;
  brandColor: string;
  features: { pwa: boolean; billing: boolean };
  localeDefault: string;
  webPushPublicKey: string | null;
  paddleEnv: string;
  pushNotifications: {
    enabled: boolean;
    vapidPublicKey: string | null;
  };
};

type SystemSettingsContextType = {
  settings: Record<string, SystemSetting>;
  publicSettings: PublicSystemSettings | null;
  isLoading: boolean;
  error: Error | null;
  getSettingValue: (key: string, defaultValue?: any) => any;
  getColorValue: (key: string, defaultValue?: string) => string;
  getBooleanValue: (key: string, defaultValue?: boolean) => boolean;
  getImageValue: (key: string, defaultValue?: string) => string;
  getJsonValue: (key: string, defaultValue?: any) => any;
};

const SystemSettingsContext = createContext<SystemSettingsContextType | null>(null);

export function SystemSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Record<string, SystemSetting>>({});
  const [publicSettings, setPublicSettings] = useState<PublicSystemSettings | null>(null);
  const { toast } = useToast();
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['/api/system-settings/public'],
    queryFn: getQueryFn<PublicSystemSettings>({ on401: 'returnNull' }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  useEffect(() => {
    if (data) {
      setPublicSettings(data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error loading settings",
        description: "Could not load system settings. Some features may not work correctly.",
        variant: "default",
      });
    }
  }, [error, toast]);

  const getSettingValue = (key: string, defaultValue: any = "") => {
    if (settings[key]) {
      return settings[key].value;
    }
    return defaultValue;
  };

  const getColorValue = (key: string, defaultValue: string = "#2563eb") => {
    return getSettingValue(key, defaultValue);
  };

  const getBooleanValue = (key: string, defaultValue: boolean = false) => {
    const value = getSettingValue(key, String(defaultValue));
    return value === "true";
  };

  const getImageValue = (key: string, defaultValue: string = "") => {
    return getSettingValue(key, defaultValue);
  };

  const getJsonValue = (key: string, defaultValue: any = null) => {
    try {
      const value = getSettingValue(key, "");
      return value ? JSON.parse(value) : defaultValue;
    } catch (err) {
      console.error(`Error parsing JSON for setting ${key}:`, err);
      return defaultValue;
    }
  };

  return (
    <SystemSettingsContext.Provider
      value={{
        settings,
        publicSettings,
        isLoading,
        error,
        getSettingValue,
        getColorValue,
        getBooleanValue,
        getImageValue,
        getJsonValue,
      }}
    >
      {children}
    </SystemSettingsContext.Provider>
  );
}

export function useSystemSettings() {
  const context = useContext(SystemSettingsContext);
  if (!context) {
    throw new Error("useSystemSettings must be used within a SystemSettingsProvider");
  }
  return context;
}